/**
 * InternalMovingFormDialog — bám Access spec
 * `frmFactoryMovementQryInternalMovingAdd` (New/Edit từ
 * `frmFactoryMovementMainSubForm` btnNew/btnEdit, mode Internal Moving).
 *
 * Layout (Access 2-col, Left=2508 trái / Left=9588 phải, theo Top):
 *   Moving Date* (date)     |  Stack Quality (display)
 *   Note*        (memo)     |  Stack Package (display)
 *   Stack ID     (combo)    |  Lot ID        (combo)   ← chọn ĐÚNG 1
 *   Current Weight (lock)   |  Current Bag   (lock)
 *   Current Area  (lock)    |  Moving Weight (lock)
 *   Moving Bag    (lock)    |  Moving Area*  (combo)
 *   Service Provider        |  Service Cost
 *
 * Required (Access `btnSave_Click`): input_date, note, current_area,
 * current_bag, current_weight, moving_area, moving_bag, moving_weight, và
 * ĐÚNG MỘT trong lot_id / stack_id (cả 2 rỗng hoặc cả 2 có → lỗi).
 *
 * Cascade (Access `stack_id_AfterUpdate` / `lot_id_AfterUpdate`): chọn Stack →
 * autofill current_weight/bag/area = balance + moving_weight/bag = balance,
 * moving_area="" , hiện Stack Quality/Package; clear Lot. Chọn Lot → tương tự
 * (không có quality/package); clear Stack. Lookups qua BE
 * `/operations/internal-moving-lookups`.
 *
 * current_* + moving_weight + moving_bag LOCKED (Access Enabled=NotDefault —
 * auto theo balance); chỉ moving_area + note + service_* user nhập tay.
 *
 * Modes: new → empty, input_date = hôm nay; edit → hydrate row (parent merge PK
 * `id`). Không có Copy (Access chỉ New/Edit/Delete).
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import type { FactoryCell, FactoryRowPayload } from '@/store/api/factoryApi'
import {
  useGetInternalMovingCombosQuery,
  useLazyGetInternalMovingLookupsQuery,
} from '@/store/api/factoryApi'
import { FORM_GRID_2COL, HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '@/theme/responsive'

type MovingRow = { id: string | number } & Record<string, FactoryCell>

interface ComboItem {
  value: string
  label: string
}

interface DraftState {
  input_date: string
  stack_id: string
  lot_id: string
  current_weight: string
  current_bag: string
  current_area: string
  moving_weight: string
  moving_bag: string
  moving_area: string
  note: string
  service_provider: string
  service_amount: string
}

const EMPTY_DRAFT: DraftState = {
  input_date: '',
  stack_id: '',
  lot_id: '',
  current_weight: '',
  current_bag: '',
  current_area: '',
  moving_weight: '',
  moving_bag: '',
  moving_area: '',
  note: '',
  service_provider: '',
  service_amount: '',
}

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}

// Readonly bg standard (skill /convert-form): neutral gray cho Access
// `Enabled = NotDefault` (locked) fields.
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': { color: 'text.secondary', fontWeight: 600 },
}

const numericInputProps = {
  style: { textAlign: 'right' as const },
  inputMode: 'decimal' as const,
}

function parseNumeric(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

// Access Format `Standard` → thousand sep, 2 decimals.
function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

interface InternalMovingFormDialogProps {
  open: boolean
  /** 'new' = empty + input_date hôm nay; 'edit' = hydrate row (PK `id` parent merge). */
  mode: 'new' | 'edit'
  row: MovingRow | null
  saving: boolean
  factoryCode: string
  /** 4-layer permission guard (memory): dialog disable Save khi false. */
  canEdit?: boolean
  onClose: () => void
  onSave: (payload: FactoryRowPayload) => void
}

const EMPTY_BAL = { nw: '', bag: '', area: '' }

export function InternalMovingFormDialog({
  open,
  mode,
  row,
  saving,
  factoryCode,
  canEdit = true,
  onClose,
  onSave,
}: InternalMovingFormDialogProps) {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  // Display columns Access: Stack group (trái) / Lot group (phải) — balance
  // NW/Bag/Area mỗi bên + stack quality/package. Auto-fill từ cascade lookup;
  // chọn bên nào fill bên đó, clear bên kia (mutual-exclusive như Access).
  const [stackQuality, setStackQuality] = useState('')
  const [stackPackage, setStackPackage] = useState('')
  const [stackBal, setStackBal] = useState(EMPTY_BAL)
  const [lotBal, setLotBal] = useState(EMPTY_BAL)

  const combosQuery = useGetInternalMovingCombosQuery({ factoryCode }, { skip: !open })
  const [triggerLookup] = useLazyGetInternalMovingLookupsQuery()

  const stackOptions = useMemo(() => combosQuery.data?.stack_id ?? [], [combosQuery.data])
  const lotOptions = useMemo(() => combosQuery.data?.lot_id ?? [], [combosQuery.data])
  const areaOptions = useMemo(() => combosQuery.data?.area ?? [], [combosQuery.data])
  // Tên nhà máy có dấu (Access factory_name RO) — nguồn tlc_factory.factory_overview
  // qua combos endpoint. Edit ưu tiên row.factory_txt (đã có sẵn trong view).
  const factoryName =
    mode === 'edit' && row?.factory_txt != null
      ? String(row.factory_txt)
      : combosQuery.data?.factory_name ?? ''

  /** Hydrate — deps `[open, mode, row]` only (memory: hydrate deps). */
  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && row) {
      setDraft({
        input_date: String(row.input_date ?? '').slice(0, 10),
        stack_id: String(row.stack_id ?? ''),
        lot_id: String(row.lot_id ?? ''),
        current_weight: formatStandard(row.current_weight as number | string | null),
        current_bag: formatStandard(row.current_bag as number | string | null),
        current_area: String(row.current_area ?? ''),
        moving_weight: formatStandard(row.moving_weight as number | string | null),
        moving_bag: formatStandard(row.moving_bag as number | string | null),
        moving_area: String(row.moving_area ?? ''),
        note: String(row.note ?? ''),
        service_provider: String(row.service_provider ?? ''),
        service_amount: formatStandard(row.service_amount as number | string | null),
      })
      // View có sẵn quality_code/package (display) → seed ngay (memory:
      // copy seed readonly from row), không chờ lazy lookup. Balance hiển thị
      // = current_* của row, đổ vào ĐÚNG bên (stack nếu có stack_id, ngược lại
      // lot) — cascade khoá ở Edit nên không refetch.
      const sideBal = {
        nw: formatStandard(row.current_weight as number | string | null),
        bag: formatStandard(row.current_bag as number | string | null),
        area: String(row.current_area ?? ''),
      }
      const hasStack = String(row.stack_id ?? '').trim().length > 0
      setStackQuality(hasStack ? String(row.quality_code ?? '') : '')
      setStackPackage(hasStack ? String(row.package ?? '') : '')
      setStackBal(hasStack ? sideBal : EMPTY_BAL)
      setLotBal(hasStack ? EMPTY_BAL : sideBal)
    } else {
      setDraft({ ...EMPTY_DRAFT, input_date: new Date().toISOString().slice(0, 10) })
      setStackQuality('')
      setStackPackage('')
      setStackBal(EMPTY_BAL)
      setLotBal(EMPTY_BAL)
    }
  }, [open, mode, row])

  /** Stack chọn → autofill balances + clear Lot (Access `stack_id_AfterUpdate`). */
  const handleStackChange = async (value: string) => {
    const sid = value.trim()
    setDraft((prev) => ({ ...prev, stack_id: sid, lot_id: '' }))
    setLotBal(EMPTY_BAL)
    if (!sid) {
      setStackQuality('')
      setStackPackage('')
      setStackBal(EMPTY_BAL)
      return
    }
    try {
      const r = await triggerLookup({ factoryCode, stackId: sid }, false).unwrap()
      setStackQuality(r.quality ?? '')
      setStackPackage(r.package ?? '')
      setStackBal({
        nw: formatStandard(r.balance_nw),
        bag: formatStandard(r.balance_bag),
        area: r.area ?? '',
      })
      setDraft((prev) => ({
        ...prev,
        stack_id: sid,
        lot_id: '',
        current_weight: formatStandard(r.balance_nw),
        current_bag: formatStandard(r.balance_bag),
        current_area: r.area ?? '',
        moving_weight: formatStandard(r.balance_nw),
        moving_bag: formatStandard(r.balance_bag),
        moving_area: '',
      }))
    } catch {
      // Lookup fail — giữ field cho user; BE raise lúc Save nếu thiếu.
    }
  }

  /** Lot chọn → autofill balances + clear Stack (Access `lot_id_AfterUpdate`). */
  const handleLotChange = async (value: string) => {
    const lid = value.trim()
    setDraft((prev) => ({ ...prev, lot_id: lid, stack_id: '' }))
    setStackQuality('')
    setStackPackage('')
    setStackBal(EMPTY_BAL)
    if (!lid) {
      setLotBal(EMPTY_BAL)
      return
    }
    try {
      const r = await triggerLookup({ factoryCode, lotId: lid }, false).unwrap()
      setLotBal({
        nw: formatStandard(r.balance_nw),
        bag: formatStandard(r.balance_bag),
        area: r.area ?? '',
      })
      setDraft((prev) => ({
        ...prev,
        lot_id: lid,
        stack_id: '',
        current_weight: formatStandard(r.balance_nw),
        current_bag: formatStandard(r.balance_bag),
        current_area: r.area ?? '',
        moving_weight: formatStandard(r.balance_nw),
        moving_bag: formatStandard(r.balance_bag),
        moving_area: '',
      }))
    } catch {
      // Lookup fail — giữ field cho user.
    }
  }

  const handleChange = <K extends keyof DraftState>(key: K, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  /** Inject value đã lưu vào options nếu combo (limit) không chứa (memory:
   *  autocomplete inject current value) → tránh hiện blank trên Edit. */
  const injectOption = (opts: ComboItem[], value: string): ComboItem | null => {
    const v = value.trim()
    if (!v) return null
    return opts.find((o) => o.value === v) ?? { value: v, label: v }
  }
  const selectedStack = useMemo(() => injectOption(stackOptions, draft.stack_id), [stackOptions, draft.stack_id])
  const selectedLot = useMemo(() => injectOption(lotOptions, draft.lot_id), [lotOptions, draft.lot_id])
  // Moving Area phải KHÁC Current Area (di chuyển sang khu khác) → loại
  // current_area khỏi options chọn (yêu cầu chị Đẹp; Access không ràng buộc).
  const movingAreaOptions = useMemo(
    () => areaOptions.filter((o) => o.value !== draft.current_area.trim()),
    [areaOptions, draft.current_area],
  )
  const selectedMovingArea = useMemo(
    () => injectOption(movingAreaOptions, draft.moving_area),
    [movingAreaOptions, draft.moving_area],
  )

  // Access `btnSave_Click` validation.
  const dateEmpty = draft.input_date.trim().length === 0
  const noteEmpty = draft.note.trim().length === 0
  const stackFilled = draft.stack_id.trim().length > 0
  const lotFilled = draft.lot_id.trim().length > 0
  // ĐÚNG 1 trong stack/lot (Access: cả 2 rỗng hoặc cả 2 đầy → errCheck=3).
  const lotStackInvalid = stackFilled === lotFilled
  const currentInvalid =
    !draft.current_area.trim() || !draft.current_bag.trim() || !draft.current_weight.trim()
  const movingInvalid =
    !draft.moving_area.trim() || !draft.moving_bag.trim() || !draft.moving_weight.trim()
  // Moving Area không được trùng Current Area (yêu cầu chị Đẹp).
  const movingAreaSame =
    draft.moving_area.trim().length > 0 &&
    draft.moving_area.trim() === draft.current_area.trim()

  const canSave =
    canEdit &&
    !dateEmpty &&
    !noteEmpty &&
    !lotStackInvalid &&
    !currentInvalid &&
    !movingInvalid &&
    !movingAreaSame &&
    !saving

  const buildPayload = (): FactoryRowPayload => {
    const str = (v: string): string | null => {
      const s = v.trim()
      return s.length ? s : null
    }
    return {
      input_date: str(draft.input_date),
      lot_id: str(draft.lot_id),
      stack_id: str(draft.stack_id),
      current_weight: parseNumeric(draft.current_weight),
      moving_weight: parseNumeric(draft.moving_weight),
      current_bag: parseNumeric(draft.current_bag),
      moving_bag: parseNumeric(draft.moving_bag),
      current_area: str(draft.current_area),
      moving_area: str(draft.moving_area),
      note: str(draft.note),
      service_provider: str(draft.service_provider),
      service_amount: parseNumeric(draft.service_amount),
    }
  }

  const handleSave = () => {
    if (!canSave) return
    onSave(buildPayload())
  }

  const titleVerb = mode === 'new' ? 'New' : 'Edit'

  // Access `btnEdit_Click` khóa input_date/lot_id/stack_id (`.Enabled=False`);
  // `btnNew_Click` mở (`.Enabled=True`). → Edit lock 3 field định danh này.
  const isEdit = mode === 'edit'

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        onClose()
      }}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: { maxHeight: 'min(760px, 92vh)', width: '100%', display: 'flex', flexDirection: 'column' },
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography
          component="span"
          noWrap
          title={`${titleVerb} — Factory · Internal Moving`}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {titleVerb} —
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            {' Factory ·'}
          </Box>
          {' Internal Moving'}
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {factoryCode.toUpperCase()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!canSave}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={onClose} disabled={saving} sx={titleBtnSx}>
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent dividers sx={{ ...compactInputSx, overflowY: 'auto', flex: '1 1 auto', minHeight: 0, pt: 1.5 }}>
        {lotStackInvalid ? (
          <Alert severity="info" sx={{ mb: 1.25, py: 0.25 }}>
            Pick exactly one source — either a Stack ID or a Lot ID (not both).
          </Alert>
        ) : null}

        {/* Grid bám cấu trúc Access `frmFactoryMovementQryInternalMovingAdd`
            (2 cột: Stack group TRÁI / Lot group PHẢI). Current Weight/Bag +
            Service Cost ẩn (Access Visible=No) → giữ trong payload, không render. */}
        <Box sx={FORM_GRID_2COL}>
          {/* Row 1 — Factory Code | Factory Name (RO header) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 1 }, ...readonlyFieldSx }}
            label="Factory Code"
            size="small"
            fullWidth
            value={factoryCode}
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 1 }, ...readonlyFieldSx }}
            label="Factory Name"
            size="small"
            fullWidth
            value={factoryName}
            InputProps={{ readOnly: true }}
          />

          {/* Row 2 — Moving Date* (lock-on-edit) */}
          <DatePicker
            label="Moving Date"
            format="DD-MMM-YYYY"
            value={draft.input_date ? dayjs(draft.input_date) : null}
            onChange={(d) => handleChange('input_date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
            disabled={isEdit}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                required: true,
                InputLabelProps: { shrink: true },
                sx: { gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 2 } },
              },
            }}
          />

          {/* Row 3 — Note* (full-width, merge 2 cột) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: '1 / -1' }, gridRow: { sm: 3 } }}
            label="Note"
            size="small"
            fullWidth
            required
            error={noteEmpty}
            value={draft.note}
            onChange={(e) => handleChange('note', e.target.value)}
          />

          {/* Row 4 — Stack ID (combo) | Lot ID (combo) — chọn ĐÚNG 1 */}
          <Autocomplete<ComboItem, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 4 } }}
            size="small"
            disabled={isEdit}
            options={stackOptions}
            loading={combosQuery.isFetching}
            value={selectedStack}
            getOptionLabel={(o) => (o.value === o.label || !o.label ? o.value : `${o.value} — ${o.label}`)}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_, v) => void handleStackChange(v?.value ?? '')}
            renderInput={(params) => (
              <TextField {...params} label="Stack ID" fullWidth helperText="Stack or Lot — pick one." />
            )}
          />
          <Autocomplete<ComboItem, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 4 } }}
            size="small"
            disabled={isEdit}
            options={lotOptions}
            loading={combosQuery.isFetching}
            value={selectedLot}
            getOptionLabel={(o) => (o.value === o.label || !o.label ? o.value : `${o.value} — ${o.label}`)}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_, v) => void handleLotChange(v?.value ?? '')}
            renderInput={(params) => <TextField {...params} label="Lot ID" fullWidth />}
          />

          {/* Row 5 — Stack Balance NW | Lot Balance NW (RO) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 5 }, ...readonlyFieldSx }}
            label="Stack Balance NW"
            size="small"
            fullWidth
            value={stackBal.nw}
            inputProps={numericInputProps}
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 5 }, ...readonlyFieldSx }}
            label="Lot Balance NW"
            size="small"
            fullWidth
            value={lotBal.nw}
            inputProps={numericInputProps}
            InputProps={{ readOnly: true }}
          />

          {/* Row 6 — Stack Balance Bag | Lot Balance Bag (RO) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 6 }, ...readonlyFieldSx }}
            label="Stack Balance Bag"
            size="small"
            fullWidth
            value={stackBal.bag}
            inputProps={numericInputProps}
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 6 }, ...readonlyFieldSx }}
            label="Lot Balance Bag"
            size="small"
            fullWidth
            value={lotBal.bag}
            inputProps={numericInputProps}
            InputProps={{ readOnly: true }}
          />

          {/* Row 7 — Stack Area | Lot Area (RO) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 7 }, ...readonlyFieldSx }}
            label="Stack Area"
            size="small"
            fullWidth
            value={stackBal.area}
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 7 }, ...readonlyFieldSx }}
            label="Lot Area"
            size="small"
            fullWidth
            value={lotBal.area}
            InputProps={{ readOnly: true }}
          />

          {/* Row 8 — Stack Quality | Current Area (RO; Current Weight/Bag ẩn) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 8 }, ...readonlyFieldSx }}
            label="Stack Quality"
            size="small"
            fullWidth
            value={stackQuality}
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 8 }, ...readonlyFieldSx }}
            label="Current Area"
            size="small"
            fullWidth
            value={draft.current_area}
            InputProps={{ readOnly: true }}
          />

          {/* Row 9 — Stack Package (RO) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 9 }, ...readonlyFieldSx }}
            label="Stack Package"
            size="small"
            fullWidth
            value={stackPackage}
            InputProps={{ readOnly: true }}
          />

          {/* Row 10 — Moving Weight | Moving Bag (RO — auto theo balance) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 10 }, ...readonlyFieldSx }}
            label="Moving Weight"
            size="small"
            fullWidth
            value={draft.moving_weight}
            inputProps={numericInputProps}
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 10 }, ...readonlyFieldSx }}
            label="Moving Bag"
            size="small"
            fullWidth
            value={draft.moving_bag}
            inputProps={numericInputProps}
            InputProps={{ readOnly: true }}
          />

          {/* Row 11 — Moving Area* (combo) | Service Provider */}
          <Autocomplete<ComboItem, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 11 } }}
            size="small"
            options={movingAreaOptions}
            loading={combosQuery.isFetching}
            value={selectedMovingArea}
            getOptionLabel={(o) => o.label || o.value}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_, v) => handleChange('moving_area', v?.value ?? '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Moving Area"
                required
                error={!draft.moving_area.trim() || movingAreaSame}
                helperText={movingAreaSame ? 'Moving Area must differ from Current Area' : undefined}
                fullWidth
              />
            )}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 11 } }}
            label="Service Provider"
            size="small"
            fullWidth
            value={draft.service_provider}
            onChange={(e) => handleChange('service_provider', e.target.value)}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default InternalMovingFormDialog
